import type * as React from 'react';
import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Box } from '../../components/ui/box';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Text } from '../../components/ui/text';
import {
  Toolbar,
  ToolbarButton,
  ToolbarSeparator,
} from '../../components/ui/toolbar';
import { useIsMobile } from '../../hooks/use-media-query';
import { DND_DRAGGABLE_PROPS } from '../../lib/dnd-attrs';

import {
  useKanbanColumnItems,
  useKanbanDrag,
  useKanbanSelection,
} from './kanban-context';
import { cardDndId } from './kanban-ids';
import type { Id } from './types';
import type { Omittable } from "../../lib/props";

export type KanbanCardProps<TItem> = Omittable<{
  item: TItem;
  columnId: Id;
  /**
   * Overrides the sortable id (default `cardDndId(columnId, itemId)`). The
   * destination-column drag phantom sets this to the ACTIVE card's own id so
   * dnd-kit recognizes it as the active node — which both lets
   * `verticalListSortingStrategy` open the cross-column gap and keeps it out of
   * collision detection (a mismatched id makes it a rogue drop target and the
   * over-column flip-flops). `data.columnId` stays the rendered column.
   */
  dndId?: Id;
  isDragOverlay?: boolean;
  /** Item id of the card above this one (for midpoint calculation). */
  upperNeighborId?: Id;
  /** Sort value of the card above this one. */
  upperSortValue?: number;
  /** Item id of the card below this one. */
  lowerNeighborId?: Id;
  /** Sort value of the card below this one. */
  lowerSortValue?: number;
}>;

/**
 * Sortable wrapper ONLY — zero card-field logic. Owns the dnd-kit wiring
 * (sortable transforms, click-vs-drag disambiguation) and the selection
 * chrome (the hover-revealed top-right cluster + group-drag visuals), and
 * delegates the card body to `renderCard` from context. Generic over `TItem`.
 */
export function KanbanCard<TItem, TColumn>({
  item,
  columnId,
  dndId,
  isDragOverlay,
  upperNeighborId,
  upperSortValue,
  lowerNeighborId,
  lowerSortValue,
}: KanbanCardProps<TItem>) {
  const {
    getItemId,
    getSortValue,
    renderCard,
    onCardClick,
    draggable,
    isItemDragDisabled,
    dragGroupIds,
  } = useKanbanDrag<TItem, TColumn>();
  const selection = useKanbanSelection<TItem>();
  const columnItems = useKanbanColumnItems<TItem>();
  const isMobile = useIsMobile();

  const dragDisabled = isItemDragDisabled?.(item) ?? false;
  const itemId = getItemId(item);
  const isSelected = selection?.selectedItemIds.has(itemId) ?? false;

  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: dndId ?? cardDndId(columnId, itemId),
    data: {
      kind: 'item',
      item,
      columnId,
      sortValue: getSortValue(item),
      upperNeighborId,
      upperSortValue,
      lowerNeighborId,
      lowerSortValue,
    },
    disabled: isDragOverlay || isMobile || dragDisabled || !draggable,
  });

  // Group-drag renderings. The active card's slots (its source position AND
  // the destination phantom, which registers under the active dnd id) both
  // report `isDragging`, so they collapse to ONE compact placeholder — the
  // whole selection previews as a single "N items" card, not N gaps. The other
  // selected cards stay put and dim like the active card's single-drag slot.
  const isGroupDrag = dragGroupIds != null;
  const showGroupPlaceholder = isGroupDrag && isDragging && !isDragOverlay;
  const isCoDragged =
    isGroupDrag && !isDragging && !isDragOverlay && dragGroupIds.has(itemId);

  // Transition only while actively sorting so cards shift smoothly; on drop, no
  // transition prevents a "snap back" animation since the optimistic cache
  // update already places items in their new positions.
  const dndTransform = CSS.Transform.toString(transform) ?? undefined;
  const dndTransition = isSorting ? transition : undefined;
  const dndOpacity = showGroupPlaceholder
    ? undefined
    : isDragging || isCoDragged
      ? 0.2
      : undefined;
  const dndCursor = isDragOverlay ? 'grabbing' : draggable ? 'grab' : undefined;

  const inert = isMobile || !draggable;

  // Dynamic dnd values (per-frame transforms, measured transitions) are the
  // legitimate inline-style case — never classes.
  const dndStyle: React.CSSProperties = {
    transform: dndTransform,
    transition: dndTransition,
    opacity: dndOpacity,
    cursor: dndCursor,
  };

  const selectable = selection != null && selection.isItemSelectable(item);
  // The consumer's actions slot can be a heavy subtree (a menu with a portal
  // trigger per card), and it is only ever revealed on hover/focus — so it
  // mounts on the first hover/focus instead of on every windowed card. The
  // checkbox stays mounted; its reveal is pure CSS.
  const [actionsArmed, setActionsArmed] = useState(false);
  const armActions = () => {
    if (!actionsArmed && selection != null && !isDragOverlay) {
      setActionsArmed(true);
    }
  };
  const actionsNode =
    actionsArmed && selection != null && !isDragOverlay
      ? selection.cardActions(item)
      : null;
  const showCluster =
    !isDragOverlay &&
    !showGroupPlaceholder &&
    (selectable || actionsNode != null);

  return (
    <Box
      ref={setNodeRef}
      fill="width"
      shrink
      position="relative"
      group="card"
      data-selected={isSelected ? "" : undefined}
      // dnd-kit's sortable attributes make this node focusable (tabIndex 0),
      // so a dialog opened from the card restores focus HERE on close — and
      // the next keydown (holding Shift to range-select) upgrades that focus
      // to :focus-visible, painting the global outline on a node with no
      // keyboard interaction design. Same reset BOX_BUTTON_RESET applies.
      className="outline-none"
      style={dndStyle}
      onPointerEnter={armActions}
      onFocus={armActions}
      onClick={(event: React.MouseEvent<HTMLElement>) => {
        // React portals bubble through the REACT tree, not the DOM tree: a
        // click inside an overlay a card field opened (a dialog, a select
        // popup) re-fires here even though the DOM node lives under
        // document.body. Only a click physically inside the card counts.
        if (!event.currentTarget.contains(event.target as Node)) return;
        if (isDragging) return;
        // Cmd/ctrl+click toggles this card alone and makes it the pivot the
        // next shift+click ranges from — the Finder model. The checkbox is
        // the discoverable affordance, the modifier clicks are the fast ones.
        if ((event.metaKey || event.ctrlKey) && selection != null && selectable) {
          selection.onItemSelectedChange(item, !isSelected);
          selection.setSelectionAnchor(columnId, itemId);
          return;
        }
        // Shift+click anywhere on the card selects the range from the pivot
        // in this column, REPLACING the pivot's previous shift range; with no
        // usable pivot it toggles this card (and becomes the pivot).
        if (event.shiftKey && selection != null && selectable) {
          const ranged =
            columnItems != null &&
            selection.selectRangeTo(item, columnId, columnItems());
          if (!ranged) {
            selection.onItemSelectedChange(item, !isSelected);
            selection.setSelectionAnchor(columnId, itemId);
          }
          return;
        }
        onCardClick?.(item);
      }}
      {...(inert ? {} : DND_DRAGGABLE_PROPS)}
      {...(inert ? {} : (attributes as Record<string, unknown>))}
      {...(inert ? {} : (listeners as Record<string, unknown>))}
    >
      {showGroupPlaceholder ? (
        <Box
          surface="card"
          rounded="md"
          align="center"
          justify="center"
          height={56}
          className="border"
        >
          <Text size="sm" variant="muted">
            {selection?.dragGroupLabel(dragGroupIds.size) ??
              `${dragGroupIds.size} items`}
          </Text>
        </Box>
      ) : (
        renderCard(item, { isDragOverlay: !!isDragOverlay, isSelected })
      )}
      {showCluster && (
        <Toolbar
          // The Toolbar's own chrome is transparent; the fading popover-surface
          // layer below paints it, so a selected card at rest shows only its
          // checkbox while the container and actions surface on hover.
          className="absolute top-1.5 right-1.5 gap-0.5 rounded-md border-transparent bg-transparent p-0.5"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          // Keep the PointerSensor from arming a drag under the controls; the
          // click above stops `onCardClick`.
          onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
        >
          {/* Painted first among the positioned children, so it sits behind
              them. */}
          <Box
            aria-hidden
            position="absolute"
            surface="popover"
            rounded="md"
            hoverReveal="card"
            revealOnTouch
            className="inset-0 border"
          />
          {actionsNode != null && (
            <Box
              direction="row"
              align="center"
              position="relative"
              hoverReveal="card"
              revealOnTouch
            >
              {actionsNode}
            </Box>
          )}
          {actionsNode != null && selectable && (
            <Box position="relative" hoverReveal="card" revealOnTouch>
              <ToolbarSeparator orientation="vertical" className="h-4" />
            </Box>
          )}
          {selectable && (
            <Box
              position="relative"
              align="center"
              justify="center"
              hoverReveal={isSelected ? undefined : 'card'}
              revealOnTouch={isSelected ? undefined : true}
            >
              {/* The whole ghost-button segment is the toggle; the checkbox
                  inside is purely a visual (span render, aria-hidden), so no
                  interactive control nests inside the button. */}
              <ToolbarButton
                aria-label="Select card"
                aria-pressed={isSelected}
                onClick={() => {
                  selection.onItemSelectedChange(item, !isSelected);
                  selection.setSelectionAnchor(columnId, itemId);
                }}
                render={<Button variant="ghost" size="icon-sm" />}
              >
                <Checkbox
                  aria-hidden
                  checked={isSelected}
                  className="pointer-events-none"
                  nativeButton={false}
                  render={<Box as="span" />}
                  tabIndex={-1}
                />
              </ToolbarButton>
            </Box>
          )}
        </Toolbar>
      )}
    </Box>
  );
}
